import { useState, useCallback, useMemo, useEffect, useRef } from 'react'

import { TreeItemIndex, TreeItem } from 'react-complex-tree'

import { TreeItemData } from '../types.js'

export function useTreeViewState({
  initialExpanded,
  activeItem,
  autoExpand,
}: {
  initialExpanded?: TreeItemIndex[]
  activeItem?: TreeItemIndex
  autoExpand?: TreeItemIndex[]
}) {
  const [focusedItem, setFocusedItem] = useState<TreeItemIndex | null>(null)
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>(
    initialExpanded ?? [],
  )
  const prevAutoExpandRef = useRef<TreeItemIndex[]>([])

  useEffect(() => {
    const prevAuto = prevAutoExpandRef.current
    setExpandedItems(prev => {
      const stale = new Set(prevAuto.filter(i => !autoExpand?.includes(i)))
      const filtered = stale.size > 0 ? prev.filter(i => !stale.has(i)) : prev
      const toAdd = autoExpand?.filter(i => !filtered.includes(i)) ?? []
      if (!toAdd.length && filtered.length === prev.length) {
        return prev
      }
      return toAdd.length ? [...filtered, ...toAdd] : filtered
    })
    prevAutoExpandRef.current = autoExpand ?? []
  }, [autoExpand])

  const [localSelectedItems, setLocalSelectedItems] = useState<TreeItemIndex[]>(
    [],
  )

  const selectedItems = useMemo(
    () => (activeItem ? [activeItem] : localSelectedItems),
    [activeItem, localSelectedItems],
  )

  const onFocusItem = useCallback((item: TreeItem<TreeItemData>) => {
    setFocusedItem(item.index)
  }, [])

  const onExpandItem = useCallback(
    (item: TreeItem<TreeItemData>) => {
      setExpandedItems(prev => [...prev, item.index])
    },
    [setExpandedItems],
  )

  const onCollapseItem = useCallback(
    (item: TreeItem<TreeItemData>) => {
      setExpandedItems(prev =>
        prev.filter(expandedItemIndex => expandedItemIndex !== item.index),
      )
    },
    [setExpandedItems],
  )

  const onSelectItems = useCallback((items: TreeItemIndex[]) => {
    setLocalSelectedItems(items)
  }, [])

  const viewState = useMemo(
    () => ({ focusedItem, expandedItems, selectedItems }),
    [focusedItem, expandedItems, selectedItems],
  )

  return {
    viewState,
    onFocusItem,
    onExpandItem,
    onCollapseItem,
    onSelectItems,
  }
}
